import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';

/**
 * Reusable hook for confirming closing date changes.
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after a successful request; receives API response
 * @param {Function} [options.onSubmitStart] - Called when submission starts (e.g. sync external loading state)
 * @param {Function} [options.onSubmitEnd] - Called when submission ends
 * @param {boolean} [options.alwaysCloseOnSubmitEnd=false] - Close modal in finally (ServiceCard behavior)
 */
const useClosingChangeRequest = ({
  onSuccess,
  onSubmitStart,
  onSubmitEnd,
  alwaysCloseOnSubmitEnd = false,
  viewAsOrganizationId
} = {}) => {
  const [state, setState] = useState({
    isOpen: false,
    submitting: false,
    serviceId: null,
    values: null
  });

  const open = useCallback(input => {
    let serviceId;

    if (input && typeof input === 'object' && ('serviceId' in input || input.serviceId)) {
      if (input.serviceId?.newClosingDate) return;
      serviceId = input.serviceId?._id || input.serviceId;
    } else {
      serviceId = input?._id || input;
    }

    if (!serviceId) return;

    setState({
      isOpen: true,
      submitting: false,
      serviceId,
      values: {}
    });
  }, []);

  const close = useCallback(() => {
    setState({
      isOpen: false,
      submitting: false,
      serviceId: null,
      values: null
    });
  }, []);

  const updateField = useCallback((key, value) => {
    setState(prev => ({
      ...prev,
      values: prev.values ? { ...prev.values, [key]: value } : { [key]: value }
    }));
  }, []);

  const submit = useCallback(async () => {
    const { serviceId, values } = state;
    if (!serviceId || values === null) return;

    const { changeNotes, newClosingDate } = values;
    if ([changeNotes, newClosingDate].some(v => !v)) {
      return toast.error('Please provide both a note and closing date!');
    }

    setState(prev => ({ ...prev, submitting: true }));
    onSubmitStart?.();

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_REQUEST_CHANGE(serviceId),
        method: 'POST',
        requestBody: {
          note: changeNotes,
          newClosingDate,
          ...(viewAsOrganizationId ? { viewAsOrganizationId } : {})
        }
      });

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Closing date updated');
      close();
      onSuccess?.(response, {
        serviceId,
        values
      });
    } catch (e) {
      toast.error(e);
    } finally {
      if (alwaysCloseOnSubmitEnd) {
        close();
      }
      setState(prev => ({ ...prev, submitting: false }));
      onSubmitEnd?.();
    }
  }, [
    state,
    close,
    onSuccess,
    onSubmitStart,
    onSubmitEnd,
    alwaysCloseOnSubmitEnd,
    viewAsOrganizationId
  ]);

  return {
    state,
    open,
    close,
    updateField,
    submit
  };
};

export default useClosingChangeRequest;
